import React, { useEffect } from 'react';
import { useIntl } from 'react-intl';
import { translate } from '@optimusgps/optimus-intl';
import { Badge, Card, Table } from 'antd';
import useFetchData from '@/OptimusRoutes/hooks/useFetchData.hook';
import { Filter } from '@/OptimusRoutes/interfaces';
import { formatDate } from '@/utilities/dateUtils';
import { GetEmailLogEventsResponseDto, OutboundEmailLog } from '../diagnostics.dto';
import DiagnosticsService from '../diagnostics.service';

type EmailEventsLogProps = {
	providerMessageId: string;
	recipientEmail: string;
};

export const EmailEventsLog = ({ providerMessageId, recipientEmail }: EmailEventsLogProps) => {
	const intl = useIntl();

	const {
		data,
		loading,
		total,
		updatePagination,
		pagination,
		loadData: fetchEmailLogEvents,
	} = useFetchData<GetEmailLogEventsResponseDto>({
		service: {
			get: (filter: Filter<OutboundEmailLog>) =>
				DiagnosticsService.getEmailLogEvents({
					...filter,
					filter: {
						where: {
							providerMessageId,
							recipientEmail,
						},
						order: {
							eventAt: 'DESC',
						},
					},
				}),
		},
		pageSize: 5,
	});

	useEffect(() => {
		fetchEmailLogEvents();
	}, [providerMessageId, recipientEmail]);

	const handleTableChange = (paginationConfig: { current?: number; pageSize?: number }) => {
		updatePagination({
			page: paginationConfig.current || 1,
			size: paginationConfig.pageSize || 5,
		});
	};

	const columns = [
		{
			title: intl.formatMessage(translate('processedOn')),
			dataIndex: 'eventAt',
			key: 'eventAt',
			render: (value: Date) => formatDate('M/D/YYYY HH:mm:ss', value, true),
		},
		{
			title: intl.formatMessage(translate('event')),
			dataIndex: 'mappedStatus',
			key: 'mappedStatus',
			render: (value: string) => (
				<>
					<Badge status="success" />
					<span className="email-logs__event-status-text">
						{intl.formatMessage(translate(value?.toLowerCase()))}
					</span>
				</>
			),
		},
	];

	return (
		<Card title={intl.formatMessage(translate('eventsLog'))} className="email-logs__event-log-card">
			<Table
				size="small"
				columns={columns}
				dataSource={data}
				loading={loading}
				rowKey={(record, index) => `${record.eventAt}-${index}`}
				pagination={{
					total,
					current: pagination.page,
					pageSize: pagination.size,
					showSizeChanger: false,
				}}
				onChange={handleTableChange}
			/>
		</Card>
	);
};
